import { GetStaticProps } from 'next';
import React, { useEffect } from 'react';

import RichText from 'components/RichText/RichText';
import useOverElement from 'hooks/useOverElement';
import { useCopyStore } from 'store';
import { CopyStoreType } from 'store/copy.types';
import { queryCMS } from 'utils/cms/api';
import { accessibilityPage } from 'utils/cms/gql';
import { ISR_TIMEOUT } from 'utils/config';
import { marketPageId, MarketPages } from 'utils/sharedStrapiQueries/market';
import { SeoCMS } from 'utils/sharedStrapiQueries/sharedTypes';
import { pageMotionProps } from 'utils/styles/animations';

import * as S from './Accessibility.styles';

type AccessibilityType = {
  heading: string;
  subheading: string;
  accessibilityTextBlock: Array<{ heading: string; subheading: string }>;
  seo: SeoCMS;
};

export interface AccessibilityProps {
  initialCopy: {
    head: CopyStoreType['copy']['head'];
    global: CopyStoreType['copy']['global'];
    globalSettings: CopyStoreType['copy']['globalSettings'];
    redirectPopup: CopyStoreType['copy']['redirectPopup'];
    cookieBar: CopyStoreType['copy']['cookieBar'];
    navbar: CopyStoreType['copy']['navbar'];
    footer: CopyStoreType['copy']['footer'];
  };
  page: AccessibilityType;
}

const defaultProps: Partial<AccessibilityProps> = {};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const id = await marketPageId(MarketPages.accessibilityPage, locale);
  const cookieId = await marketPageId(MarketPages.cookieBar, locale);
  const footerId = await marketPageId(MarketPages.footer, locale);

  const globalIds = { cookieId, footerId };

  const response = await queryCMS({
    query: accessibilityPage({ id, locale, globalIds }),
  });

  const {
    data: {
      sharedCopies,
      accessibilityPage: page,
      redirectPopups,
      cookieBar,
      navbar,
      footer,
    },
  } = response;

  if (!page || !sharedCopies) return { notFound: true };

  return {
    props: {
      initialCopy: {
        head: page?.seo || null,
        global: sharedCopies[0]?.global || null,
        globalSettings: sharedCopies[0] || null,
        redirectPopup: redirectPopups[0] || null,
        cookieBar: cookieBar || null,
        navbar,
        footer,
      },
      page,
    },
    revalidate: ISR_TIMEOUT,
  };
};

const Accessibility: React.FunctionComponent<AccessibilityProps> = ({
  initialCopy,
  page,
}) => {
  const { setCopy } = useCopyStore();

  const [headingRef] = useOverElement();
  const [contentRef] = useOverElement();

  useEffect(() => {
    setCopy(initialCopy);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCopy]);

  const textBlocks = page.accessibilityTextBlock.map(
    ({ heading, subheading }) => (
      <S.TextBlock key={heading}>
        <S.Heading size="heading4" text={heading} />
        <S.RichTextWrapper>
          <RichText defaultSpacing richText={subheading} />
        </S.RichTextWrapper>
      </S.TextBlock>
    )
  );

  return (
    <S.Wrapper {...pageMotionProps}>
      <S.HeadingWrapper ref={headingRef}>
        <S.Heading size="heading1" text={page.heading} />
        <S.Subheading size="body" text={page.subheading} />
      </S.HeadingWrapper>
      <S.ContentWrapper ref={contentRef}>{textBlocks}</S.ContentWrapper>
    </S.Wrapper>
  );
};

Accessibility.defaultProps = defaultProps;

export default Accessibility;
